import React, { useEffect, useState } from 'react';
import TopBanner from '../../../components/topBanner/topBanner';
import imgBanner from "../../../images/banner/banner-tin-tuc.jpg";
import TitleCenter from '../../../components/title/TitleCenter';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { normalizeLang } from '../../../utils/localeDataLoader';
import { getProductSolutionCategories } from '../../../apis/productSolutionCategories';
import { getProductSolutionContentsByCategory } from '../../../apis/productSolutionContents';
import { getProductSolutionGroups } from '../../../apis/productSolutionGroups';
import { normalizeUploadedImageUrl } from '../../../apis/uploadImage';
import ProductImageMockup from './ProductImageMockup';
import DynamicIcon from '../../../components/shared/DynamicIcon';
import PageNotFound from '../../../components/notfound/PageNotFound';

function DetailPSContent() {
    const { categoryID, slug } = useParams();
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const currentBasePath = location.pathname.split('/')[1];

    const [category, setCategory] = useState(null);   // Thông tin danh mục từ API
    const [groupData, setGroupData] = useState(null); // Thông tin nhóm SP (banner)
    const [contents, setContents] = useState([]);     // Nội dung từ API
    const [loading, setLoading] = useState(true);
    const [isNotFound, setIsNotFound] = useState(false);
    const [categoryId, setCategoryId] = useState(null); // Lưu trữ ID để tìm lại khi đổi ngôn ngữ
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }, []);

    useEffect(() => {
        const currentLang = normalizeLang(i18n.language);

        const loadData = async () => {
            setLoading(true);
            try {
                // ── 1. Fetch song song categories và groups từ API ──
                const [categoriesFromApi, groupsFromApi] = await Promise.all([
                    getProductSolutionCategories(currentLang),
                    getProductSolutionGroups(currentLang),
                ]);

                let foundCategory = null;
                if (Array.isArray(categoriesFromApi)) {
                    foundCategory = categoriesFromApi.find(c => c.slug === slug)
                        ?? (categoryId !== null ? categoriesFromApi.find(c => c.id === categoryId) : null);
                }

                if (foundCategory) {
                    if (foundCategory.isActive === false || foundCategory.isActive === 0) {
                        setCategory(null);
                        setContents([]);
                        setGroupData(null);
                        setIsNotFound(true);
                        return;
                    }

                    const expectedBasePath = currentLang === 'vi' ? 'san-pham-giai-phap' : 'products-solutions';
                    // Nếu slug của category hoặc group bị thay đổi (do đổi ngôn ngữ), tiến hành điều hướng tới URL mới
                    if (foundCategory.slug !== slug || foundCategory.groupSlug !== categoryID || currentBasePath !== expectedBasePath) {
                        navigate(`/${expectedBasePath}/${foundCategory.groupSlug}/${foundCategory.slug}`, { replace: true });
                    }

                    setCategory(foundCategory);
                    setCategoryId(foundCategory.id);

                    // ── 2. Dùng database ID của category để lấy contents ──
                    const contentData = await getProductSolutionContentsByCategory(
                        currentLang,
                        foundCategory.id
                    );
                    setContents(Array.isArray(contentData) ? contentData : []);
                    setIsNotFound(false);
                } else {
                    setCategory(null);
                    setContents([]);
                    setIsNotFound(true);
                }

                // ── 3. Lấy thông tin nhóm SP từ API (không dùng local data tĩnh) ──
                const actualGroupSlug = foundCategory ? foundCategory.groupSlug : categoryID;
                if (Array.isArray(groupsFromApi)) {
                    const foundGroup = groupsFromApi.find(g => g.slug === actualGroupSlug);
                    if (foundGroup) {
                        if (foundGroup.isActive === false || foundGroup.isActive === 0) {
                            setCategory(null);
                            setContents([]);
                            setGroupData(null);
                            setIsNotFound(true);
                            return;
                        }
                        setGroupData(foundGroup);
                    } else {
                        setGroupData(null);
                    }
                } else {
                    setGroupData(null);
                }

            } catch (error) {
                console.error('[DetailPSContent] Lỗi tải dữ liệu:', error);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [categoryID, slug, i18n.language, currentBasePath]); // eslint-disable-line react-hooks/exhaustive-deps

    // Helper render grouped items inside a section
    const renderSectionItems = (items = []) => {
        if (!items || items.length === 0) return null;

        const renderedElements = [];
        let cardBuffer = [];
        let imageBuffer = [];

        const flushCards = () => {
            if (cardBuffer.length > 0) {
                renderedElements.push(
                    <div className="ps-pub-cards-grid" key={`cards-grid-${renderedElements.length}`}>
                        {cardBuffer.map((card, cIdx) => {
                            const hasDesc = !!(card.content && card.content.trim());
                            return (
                                <div
                                    className={`ps-pub-card ${!hasDesc ? 'ps-pub-card--compact' : 'ps-pub-card--detailed'}`}
                                    key={card.id || cIdx}
                                >
                                    {card.badge && (
                                        <div className="ps-pub-card__badge">
                                            <DynamicIcon icon={card.badge} size={26} />
                                        </div>
                                    )}
                                    <div className="ps-pub-card__body">
                                        {card.title && <h4 className="ps-pub-card__title">{card.title}</h4>}
                                        {hasDesc && <p className="ps-pub-card__desc">{card.content}</p>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                );
                cardBuffer = [];
            }
        };

        const flushImages = () => {
            if (imageBuffer.length > 0) {
                if (imageBuffer.length === 1) {
                    const imgItem = imageBuffer[0];
                    renderedElements.push(
                        <div className="ps-pub-single-image" key={imgItem.id || `img-${renderedElements.length}`}>
                            <ProductImageMockup
                                src={imgItem.imageUrl}
                                caption={imgItem.caption}
                                title={imgItem.title}
                                badge={imgItem.badge}
                            />
                        </div>
                    );
                } else {
                    renderedElements.push(
                        <div className="ps-pub-images-grid" key={`images-grid-${renderedElements.length}`}>
                            {imageBuffer.map((imgItem, iIdx) => (
                                <div className="ps-pub-grid-image-item" key={imgItem.id || iIdx}>
                                    <ProductImageMockup
                                        src={imgItem.imageUrl}
                                        caption={imgItem.caption}
                                        title={imgItem.title}
                                        badge={imgItem.badge}
                                    />
                                </div>
                            ))}
                        </div>
                    );
                }
                imageBuffer = [];
            }
        };

        items.forEach((item, idx) => {
            if (item.itemType === 'card') {
                flushImages();
                cardBuffer.push(item);
                return;
            }

            if (item.itemType === 'image') {
                flushCards();
                if (item.imageUrl) {
                    imageBuffer.push(item);
                }
                return;
            }

            // Flush all buffers before other item types
            flushCards();
            flushImages();

            if (item.itemType === 'text_image') {
                const layout = item.layout || 'left';
                renderedElements.push(
                    <div className={`ps-pub-split ps-pub-split--${layout}`} key={item.id || idx}>
                        <div className="ps-pub-split__text">
                            {item.badge && <span className="ps-pub-item__tag">{item.badge}</span>}
                            {item.title && <h4 className="ps-pub-item__heading">{item.title}</h4>}
                            {item.content && (
                                <div className="ps-pub-item__content">
                                    {item.content.split('\n').map((line, lIdx) => line ? <p key={lIdx}>{line}</p> : null)}
                                </div>
                            )}
                        </div>
                        {item.imageUrl && (
                            <div className="ps-pub-split__media">
                                <ProductImageMockup
                                    src={item.imageUrl}
                                    caption={item.caption}
                                    title={item.title}
                                    badge={item.badge}
                                />
                            </div>
                        )}
                    </div>
                );
            } else if (item.itemType === 'text') {
                renderedElements.push(
                    <div className="ps-pub-single-text" key={item.id || idx}>
                        {item.badge && <span className="ps-pub-item__tag">{item.badge}</span>}
                        {item.title && <h4 className="ps-pub-item__heading">{item.title}</h4>}
                        {item.content && (
                            <div className="ps-pub-item__content">
                                {item.content.split('\n').map((line, lIdx) => line ? <p key={lIdx}>{line}</p> : null)}
                            </div>
                        )}
                    </div>
                );
            }
        });

        // Flush remaining
        flushCards();
        flushImages();

        return renderedElements;
    };

    if (!loading && isNotFound) {
        return <PageNotFound />;
    }

    return (
        <div className='DetailPSCategory'>
            <TopBanner
                titleCenter={t('home.products_title') || "Sản Phẩm và Giải Pháp"}
                imgBanner={imgBanner}
                pageName={groupData ? (groupData.navMenuTitle || groupData.name || '') : ""}
                text={category ? (category.name || '') : ""}
            />
            <div className='layout_container content_pages'>
                <TitleCenter text={category ? (category.name || '') : ""} />
                <div className='PScategory'>
                    <div className="row">
                        <div className='col-xs-12 col-md-12 col-lg-12'>

                            {loading && (
                                <p style={{ fontSize: '1.6rem', color: '#888', textAlign: 'center', padding: '40px 0' }}>
                                    Đang tải nội dung...
                                </p>
                            )}

                            {!loading && contents.length === 0 && (
                                <p style={{ fontSize: '1.6rem', color: '#aaa', textAlign: 'center', padding: '40px 0' }}>
                                    Chưa có nội dung mô tả.
                                </p>
                            )}

                            {!loading && contents.map((content, index) => {
                                const hasStructuredSections = Array.isArray(content.sections) && content.sections.length > 0;

                                return (
                                    <div className="ProductSolution-content-wrapper" key={content.id || index}>
                                        {/* ── 1. Structured Sections Presentation ── */}
                                        {hasStructuredSections ? (
                                            <div className="ps-structured-presentation">
                                                {content.sections.map((section, sIdx) => {
                                                    const hasHeader = section.title || section.subtitle || section.badge || section.description;
                                                    return (
                                                        <section className="ps-pub-section" key={section.id || sIdx}>
                                                            {hasHeader && (
                                                                <div className="ps-pub-section__header">
                                                                    {section.badge && (
                                                                        <span className="ps-pub-section__badge">{section.badge}</span>
                                                                    )}
                                                                    {section.title && (
                                                                        <h3 className="ps-pub-section__title">{section.title}</h3>
                                                                    )}
                                                                    {(section.subtitle || section.description) && (
                                                                        <p className="ps-pub-section__subtitle">
                                                                            {section.subtitle || section.description}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            )}

                                                            <div className="ps-pub-items-list">
                                                                {renderSectionItems(section.items)}
                                                            </div>
                                                        </section>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            /* ── 2. Standard Quill Rich Text Fallback ── */
                                            <div className="ProductSolution-content ps-standard-article">
                                                {content.description && (
                                                    <div
                                                        className="quill-content ps-description"
                                                        dangerouslySetInnerHTML={{ __html: normalizeUploadedImageUrl(content.description) }}
                                                    />
                                                )}

                                                {content.images && content.images.length > 0 && (
                                                    <div className="ps-content-images-mockups">
                                                        {[...content.images]
                                                            .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                                                            .map(img => (
                                                                <ProductImageMockup
                                                                    key={img.id}
                                                                    src={img.imageUrl}
                                                                    title={category ? (category.name || '') : 'Giao diện hệ thống'}
                                                                />
                                                            ))
                                                        }
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DetailPSContent;